import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { isDriverAuthenticated } from '../utils/driverAuthUtils';
import { clearDriverAuth } from '../features/driver/driverAuthSlice';

const DriverRoute = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const driverAuthState = useSelector((state) => state.driverAuth);
  const { isLoading } = driverAuthState;
  const [authChecked, setAuthChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Enhanced authentication check on component mount and when auth state changes
  useEffect(() => {
    const checkAuthentication = async () => {
      try {

        // Perform authentication check
        const isAuthenticated = isDriverAuthenticated(driverAuthState);

        if (!isAuthenticated) {
          // Clear any invalid auth data silently
          dispatch(clearDriverAuth());
          setRedirecting(true);
        }

        setAuthChecked(true);
      } catch (error) {
        console.error('🚨 DriverRoute: Critical error during authentication check:', error);
        dispatch(clearDriverAuth());
        setRedirecting(true);
        setAuthChecked(true);
      }
    };

    checkAuthentication();
  }, [driverAuthState, location.pathname, dispatch]);

  // Show loading state while authentication is being checked or during initial load
  if (isLoading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-6 text-gray-600 text-lg font-medium">Verifying driver authentication...</p>
          <p className="mt-2 text-gray-500 text-sm">Please wait while we secure your session</p>
        </div>
      </div>
    );
  }

  // Redirect to login if authentication failed
  if (redirecting || !isDriverAuthenticated(driverAuthState)) {
    return <Navigate to="/driver/login" replace state={{ from: location }} />;
  }

  // Additional validation for driver status
  const driver = driverAuthState.driver;
  if (driver && driver.status === 'suspended') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md">
          <div className="bg-red-100 p-6 rounded-lg">
            <h2 className="text-xl font-bold text-red-800 mb-4">Account Suspended</h2>
            <p className="text-red-600 mb-4">
              Your driver account has been suspended. Please contact support for assistance.
            </p>
            <button
              onClick={() => {
                dispatch(clearDriverAuth());
                window.location.href = '/driver/login';
              }}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Driver is authenticated and active, render the protected content

  return children;
};

export default DriverRoute;
